import { useState } from 'react';
import { validateColumnName } from '../utils/spanCoverage';

interface RenameAreaModalProps {
  industry: string;
  oldArea: string;
  /** LIVE UC count in the column, shown as a transparency hint. Supplied by
   *  IndustryMatrix from the set it actually RENDERED, which is what makes this number
   *  agree with the matrix by construction rather than by two filters happening to
   *  match. (It used to be recomputed from the page's cache, which counted tombstones —
   *  one prod column then read 7 when only 1 UC was live. The read itself stopped
   *  returning tombstones in CR-302136400.) */
  ucCount: number;
  saving: boolean;
  onClose: () => void;
  /** Resolves `null` on success, or a ready-to-show failure message classified by the
   *  hook (409 = name taken, 404 = source column gone, otherwise generic). */
  onSave: (input: { newArea: string }) => Promise<string | null>;
}

/**
 * Rename Area — ONE atomic backend op (`RenameSolutionMapArea`). Was a per-UC
 * deprecate-old + insert-new simulation, which is why this used to warn about N writes
 * and label the button "迁移中…"; nothing is migrated now, so it just saves.
 * Lock is owned by `useSolutionMapMutations`.
 */
export default function RenameAreaModal({
  industry,
  oldArea,
  ucCount,
  saving,
  onClose,
  onSave,
}: RenameAreaModalProps) {
  const [newArea, setNewArea] = useState(oldArea);
  const [error, setError] = useState<string | null>(null);

  const trimmed = newArea.trim();
  const unchanged = trimmed === oldArea;

  const handleSave = async () => {
    setError(null);
    if (!trimmed) {
      setError('新名不能为空');
      return;
    }
    if (unchanged) {
      onClose();
      return;
    }
    // Rejected locally because both failure modes are SILENT or misattributed if they
    // reach the backend — see validateColumnName for which, and why.
    const invalid = validateColumnName(trimmed);
    if (invalid) {
      setError(invalid);
      return;
    }
    const failure = await onSave({ newArea: trimmed });
    if (failure) setError(failure);
  };

  return (
    <div
      className="ivt-topic-modal__overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <div className="ivt-topic-modal" role="dialog" aria-modal="true">
        <div className="ivt-topic-modal__title">重命名 Solution Area</div>
        <div className="ivt-topic-modal__sub">
          {industry} · 共 {ucCount} 个 UC
        </div>

        <label className="ivt-topic-modal__label">Old Name</label>
        <input className="ivt-topic-modal__input" value={oldArea} disabled readOnly />

        <label className="ivt-topic-modal__label">New Name *</label>
        <input
          className="ivt-topic-modal__input"
          value={newArea}
          onChange={(e) => setNewArea(e.target.value)}
          disabled={saving}
          autoFocus
        />


        {error && (
          <div className="ivt-topic-modal__error" role="alert">
            {error}
          </div>
        )}

        <div className="ivt-topic-modal__actions">
          <button
            type="button"
            className="ivt-topic-modal__btn"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="ivt-topic-modal__btn ivt-topic-modal__btn--primary"
            onClick={handleSave}
            disabled={saving || !trimmed || unchanged}
          >
            {saving ? '保存中…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
